import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import type { Schedule, Slideshow, DayOfWeek, ScheduleType } from "./types";
import { getWeekStart, addDays, formatDateRange } from "./utils";
import { useApiClient } from "../../providers/AuthProvider";
import { useTenant } from "../../providers/TenantProvider";
import type { PermissionGroup } from "../../components/HierarchyPicker/types";

// --- Types ---

type ScheduleEditorContextType = {
  // Data
  schedules: Schedule[];
  slideshows: Slideshow[];
  groups: PermissionGroup[];
  canCrossTenantBroadcast: boolean;
  selectedId: string | null;
  hasUnsavedChanges: boolean;
  isLoading: boolean;
  error: string | null;

  // Week navigation
  weekStart: Date;
  weekLabel: string;
  goToNextWeek: () => void;
  goToPrevWeek: () => void;
  goToToday: () => void;

  // Selection
  selectSchedule: (id: string | null) => void;

  // CRUD operations (local, not yet saved)
  updateSchedule: (id: string, updates: Partial<Schedule>) => void;
  deleteSchedule: (id: string) => void;
  createScheduleFromSlideshow: (slideshowId: string, day: DayOfWeek) => void;

  // Persistence
  saveChanges: () => Promise<void>;
  discardChanges: () => void;
  reload: () => Promise<void>;

  // Helpers
  getSlideshow: (id: string) => Slideshow | undefined;
  canEditSchedule: (schedule: Schedule) => boolean;
  canEditSlideshow: (slideshow: Slideshow) => boolean;
};

const ScheduleEditorContext = createContext<ScheduleEditorContextType | null>(null);

// --- API Response Types ---

type SchedulesResponse = {
  schedules: Array<{
    id: string;
    name: string;
    slideshowId: string;
    daysOfWeek: DayOfWeek[];
    startTime: string;
    endTime: string;
    type: ScheduleType;
    startDate?: string;
    endDate?: string;
    exclusive?: boolean;
    groupId: string;
    tenantId: string;
    targetGroupIds: string[];
    canEdit: boolean;
  }>;
};

type SlideshowsResponse = {
  slideshows: Array<{
    id: string;
    name: string;
    color: string;
    groupId: string;
    canEdit: boolean;
  }>;
};

type GroupsResponse = {
  groups: PermissionGroup[];
};

type CrossTenantBroadcastResponse = {
  canBroadcast: boolean;
};

type ScheduleResponse = {
  schedule: SchedulesResponse["schedules"][0];
};

// --- Provider ---

type Props = {
  children: React.ReactNode;
};

export const ScheduleEditorProvider = ({ children }: Props) => {
  const apiClient = useApiClient();
  const { currentTenantId } = useTenant();

  // Core state
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [originalSchedules, setOriginalSchedules] = useState<Schedule[]>([]);
  const [slideshows, setSlideshows] = useState<Slideshow[]>([]);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [canCrossTenantBroadcast, setCanCrossTenantBroadcast] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track new schedules created locally (not yet saved)
  const [pendingCreates, setPendingCreates] = useState<Set<string>>(new Set());
  // Track deleted schedules (not yet saved)
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());
  // Track updated schedules (not yet saved)
  const [pendingUpdates, setPendingUpdates] = useState<Set<string>>(new Set());

  // Week navigation state - start with current week
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()));

  // Computed
  const hasUnsavedChanges = pendingCreates.size > 0 || pendingDeletes.size > 0 || pendingUpdates.size > 0;
  const weekLabel = useMemo(() => formatDateRange(weekStart), [weekStart]);

  // --- Data Loading ---

  const loadData = useCallback(async () => {
    if (!currentTenantId) return;
    setIsLoading(true);
    setError(null);

    try {
      const encodedTenantId = encodeURIComponent(currentTenantId);
      const tenantParam = `?tenantId=${encodedTenantId}`;
      const capabilityRes = await apiClient.get<CrossTenantBroadcastResponse>(
        `/api/schedules/can-cross-tenant-broadcast${tenantParam}`,
      );
      const groupsPath = capabilityRes.canBroadcast
        ? "/api/groups"
        : `/api/groups?tenantId=${encodedTenantId}&permission=schedules.create`;
      const [schedulesRes, slideshowsRes, groupsRes] = await Promise.all([
        apiClient.get<SchedulesResponse>(`/api/schedules${tenantParam}`),
        apiClient.get<SlideshowsResponse>(`/api/slideshows${tenantParam}`),
        apiClient.get<GroupsResponse>(groupsPath),
      ]);

      // Map API response to UI types
      const mappedSchedules: Schedule[] = schedulesRes.schedules.map((s) => ({
        id: s.id,
        name: s.name,
        slideshowId: s.slideshowId,
        groupId: s.groupId,
        targetGroupIds: s.targetGroupIds ?? [s.groupId],
        daysOfWeek: s.daysOfWeek,
        startTime: s.startTime,
        endTime: s.endTime,
        type: s.type,
        startDate: s.startDate,
        endDate: s.endDate,
        exclusive: s.exclusive,
        canEdit: s.canEdit,
      }));

      const mappedSlideshows: Slideshow[] = slideshowsRes.slideshows.map((s) => ({
        id: s.id,
        name: s.name,
        color: s.color,
        canEdit: s.canEdit,
      }));

      setSchedules(mappedSchedules);
      setOriginalSchedules(mappedSchedules);
      setSlideshows(mappedSlideshows);
      setGroups(groupsRes.groups);
      setCanCrossTenantBroadcast(capabilityRes.canBroadcast);
      setPendingCreates(new Set());
      setPendingDeletes(new Set());
      setPendingUpdates(new Set());
    } catch (err: any) {
      console.error("Failed to load schedule data:", err);
      setError(err.message || "Failed to load schedules");
    } finally {
      setIsLoading(false);
    }
  }, [apiClient, currentTenantId]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- Week Navigation ---

  const goToNextWeek = useCallback(() => {
    setWeekStart((current) => addDays(current, 7));
  }, []);

  const goToPrevWeek = useCallback(() => {
    setWeekStart((current) => addDays(current, -7));
  }, []);

  const goToToday = useCallback(() => {
    setWeekStart(getWeekStart(new Date()));
  }, []);

  // --- Selection ---

  const selectSchedule = useCallback((id: string | null) => {
    setSelectedId(id);
  }, []);

  // --- CRUD Operations (local) ---

  const updateSchedule = useCallback((id: string, updates: Partial<Schedule>) => {
    setSchedules((prev) => {
      const schedule = prev.find((s) => s.id === id);
      if (!schedule) return prev;

      // Check if any values actually changed
      const hasChanges = Object.entries(updates).some(([key, value]) => {
        const currentValue = schedule[key as keyof Schedule];
        // Deep compare for arrays (like daysOfWeek)
        if (Array.isArray(value) && Array.isArray(currentValue)) {
          return JSON.stringify(value) !== JSON.stringify(currentValue);
        }
        return value !== currentValue;
      });

      if (!hasChanges) return prev;

      // Mark as updated (only if actually changed)
      setPendingUpdates((pendingPrev) => {
        const next = new Set(pendingPrev);
        next.add(id);
        return next;
      });

      return prev.map((s) => (s.id === id ? { ...s, ...updates } : s));
    });
  }, []);

  const deleteSchedule = useCallback((id: string) => {
    setSchedules((prev) => prev.filter((s) => s.id !== id));
    setSelectedId((current) => (current === id ? null : current));

    // If it was a pending create, just remove from pending
    setPendingCreates((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      return prev;
    });

    // If it was an existing schedule, mark for deletion
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    // Remove from updates if present
    setPendingUpdates((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const createScheduleFromSlideshow = useCallback(
    (slideshowId: string, day: DayOfWeek) => {
      const slideshow = slideshows.find((s) => s.id === slideshowId);
      if (!slideshow) return;

      // Default new schedules to the currently-selected tenant
      if (!currentTenantId) return;

      const tempId = `temp-${crypto.randomUUID()}`;
      const newSchedule: Schedule = {
        id: tempId,
        name: slideshow.name,
        slideshowId,
        groupId: currentTenantId,
        targetGroupIds: [currentTenantId],
        daysOfWeek: [day],
        startTime: "00:00",
        endTime: "23:59",
        type: "recurring",
        exclusive: false,
        canEdit: true,
      };

      setSchedules((prev) => [...prev, newSchedule]);
      setSelectedId(tempId);
      setPendingCreates((prev) => new Set(prev).add(tempId));
    },
    [slideshows, currentTenantId],
  );

  // --- Persistence ---

  const saveChanges = useCallback(async () => {
    setError(null);

    try {
      // Process deletes
      for (const id of pendingDeletes) {
        // Skip temp IDs (they were never saved)
        if (!id.startsWith("temp-")) {
          await apiClient.delete(`/api/schedules/${id}`);
        }
      }

      // Process creates
      const idMap = new Map<string, string>(); // temp ID -> real ID
      for (const tempId of pendingCreates) {
        const schedule = schedules.find((s) => s.id === tempId);
        if (schedule) {
          const res = await apiClient.post<ScheduleResponse>("/api/schedules", {
            name: schedule.name,
            slideshowId: schedule.slideshowId,
            groupId: schedule.groupId,
            targetGroupIds: schedule.targetGroupIds,
            daysOfWeek: schedule.daysOfWeek,
            startTime: schedule.startTime,
            endTime: schedule.endTime,
            type: schedule.type,
            startDate: schedule.startDate,
            endDate: schedule.endDate,
            exclusive: schedule.exclusive,
          });
          idMap.set(tempId, res.schedule.id);
        }
      }

      // Process updates (skip pending creates and deletes)
      for (const id of pendingUpdates) {
        if (pendingCreates.has(id) || pendingDeletes.has(id)) continue;

        const schedule = schedules.find((s) => s.id === id);
        if (schedule) {
          await apiClient.put(`/api/schedules/${id}`, {
            name: schedule.name,
            slideshowId: schedule.slideshowId,
            groupId: schedule.groupId,
            targetGroupIds: schedule.targetGroupIds,
            daysOfWeek: schedule.daysOfWeek,
            startTime: schedule.startTime,
            endTime: schedule.endTime,
            type: schedule.type,
            startDate: schedule.startDate,
            endDate: schedule.endDate,
            exclusive: schedule.exclusive,
          });
        }
      }

      // Update local state with real IDs
      setSchedules((prev) =>
        prev.map((s) => {
          const realId = idMap.get(s.id);
          return realId ? { ...s, id: realId } : s;
        }),
      );

      // Update selected ID if it was a temp ID
      setSelectedId((current) => {
        if (current && idMap.has(current)) {
          return idMap.get(current)!;
        }
        return current;
      });

      // Clear pending changes
      setPendingCreates(new Set());
      setPendingDeletes(new Set());
      setPendingUpdates(new Set());

      // Reload to get fresh data
      await loadData();
    } catch (err: any) {
      console.error("Failed to save changes:", err);
      setError(err.message || "Failed to save changes");
      throw err;
    }
  }, [apiClient, schedules, pendingCreates, pendingDeletes, pendingUpdates, loadData]);

  const discardChanges = useCallback(() => {
    setSchedules([...originalSchedules]);
    setPendingCreates(new Set());
    setPendingDeletes(new Set());
    setPendingUpdates(new Set());
    setSelectedId(null);
    setError(null);
  }, [originalSchedules]);

  // --- Helpers ---

  const getSlideshow = useCallback((id: string) => slideshows.find((s) => s.id === id), [slideshows]);

  const canEditSchedule = useCallback((schedule: Schedule) => schedule.canEdit, []);

  const canEditSlideshow = useCallback((slideshow: Slideshow) => slideshow.canEdit, []);

  // --- Context Value ---

  const value: ScheduleEditorContextType = {
    schedules,
    slideshows,
    groups,
    canCrossTenantBroadcast,
    selectedId,
    hasUnsavedChanges,
    isLoading,
    error,
    weekStart,
    weekLabel,
    goToNextWeek,
    goToPrevWeek,
    goToToday,
    selectSchedule,
    updateSchedule,
    deleteSchedule,
    createScheduleFromSlideshow,
    saveChanges,
    discardChanges,
    reload: loadData,
    getSlideshow,
    canEditSchedule,
    canEditSlideshow,
  };

  return <ScheduleEditorContext.Provider value={value}>{children}</ScheduleEditorContext.Provider>;
};

// --- Hook ---

export const useScheduleEditor = (): ScheduleEditorContextType => {
  const context = useContext(ScheduleEditorContext);
  if (!context) {
    throw new Error("useScheduleEditor must be used within a ScheduleEditorProvider");
  }
  return context;
};

// Convenience hook for selected schedule
export const useSelectedSchedule = (): Schedule | undefined => {
  const { schedules, selectedId } = useScheduleEditor();
  return useMemo(() => schedules.find((s) => s.id === selectedId), [schedules, selectedId]);
};
